import { CameraManager } from './modules/CameraManager';
import { FileSource } from './modules/FileSource';
import { HudController, type AppMode, type Settings } from './modules/HudController';
import { computeJointAngles, JOINT_DEFINITIONS } from './modules/JointAngles';
import { anglesToCsv, nodesToCsv, posesToJson } from './modules/PoseExport';
import { encodePoseBinary } from './modules/PoseBinaryFormat';
import { PoseRecordStore } from './modules/PoseRecordStore';
import { PoseSmoother } from './modules/PoseSmoother';
import { CroppedPoseOrchestrator } from './modules/CroppedPoseOrchestrator';
import { MediaPipePoseEngine } from './modules/MediaPipePoseEngine';
import { MediaPipePoseEngineWorker } from './modules/MediaPipePoseEngineWorker';
import { MoveNetPoseEngine } from './modules/MoveNetPoseEngine';
import { MoveNetPoseEngineWorker } from './modules/MoveNetPoseEngineWorker';
import { RTMPoseEngine } from './modules/RTMPoseEngine';
import { PerformanceController } from './modules/PerformanceController';
import type { PoseEngine } from './modules/PoseEngine';
import { zipSync, strToU8 } from 'fflate';
import { FileSystemSaver } from './modules/FileSystemSaver';
import { KeepTabActive } from './modules/KeepTabActive';
import { Recorder, downloadBlob } from './modules/Recorder';
import { RuntimeProfiler } from './modules/RuntimeProfiler';
import { WakeLockGuard } from './modules/WakeLock';
import { newRecordingId, pruneOldRecordings, saveRecording } from './viewer/RecordingStore';
import { requireSignIn } from './auth/client';
import { buildCanonicalBinFromPoseBin } from './persistence';
import { pickPoseLifter } from './adapters/onnx-pose-lifter';
import { pickDepthEstimator } from './adapters/onnx-depth';
import { lastPickedOnnxEP } from './adapters/onnx-runtime';
import {
  buildGravityRotation,
  getGravityPermissionState,
  getGravityReading,
  requestGravityPermission,
} from './modules/GravityAnchor';
import {
  markRecordingFailed,
  markRecordingReady,
  markRecordingSaving,
} from './viewer/RecordingStatus';
import { Renderer } from './modules/Renderer';
import { ScreenSource, normalizeYouTubeUrl, toYouTubeEmbedUrl } from './modules/ScreenSource';
import { filterByScore, mapRawToBodies } from './modules/SkeletonMapper';
import { CocoSsdDetector } from './modules/tracking/CocoSsdDetector';
import { CocoSsdDetectorWorker } from './modules/tracking/CocoSsdDetectorWorker';
import {
  DeepReIdExtractor,
  type DeepReIdExtractorLike,
} from './modules/tracking/DeepReIdExtractor';
import { DeepReIdExtractorWorker } from './modules/tracking/DeepReIdExtractorWorker';
import type { Detector } from './modules/tracking/Detector';
import { EdgeDetector } from './modules/tracking/EdgeDetector';
import { OnnxReidExtractor } from './modules/tracking/OnnxReidExtractor';
import { YoloDetector } from './modules/tracking/YoloDetector';
import { BASKETBALL_COURT, CourtCalibrator } from './modules/tracking/CourtCalibrator';
import { CourtView } from './modules/tracking/CourtView';
import { HeatmapRenderer } from './modules/tracking/HeatmapRenderer';
import { sampleFeature, type ReIdFeature } from './modules/tracking/ReIdFeatures';
import { TeamClassifier, sampleUpperBodyColor } from './modules/tracking/TeamClassifier';
import { Tracker, type TrackedObject } from './modules/tracking/Tracker';
import { TrackTagStore } from './modules/tracking/TrackTagStore';
import { TrackingRenderer } from './modules/tracking/TrackingRenderer';
import { trajectoriesToCsv, trajectoriesToJson } from './modules/tracking/TrajectoryExport';
import { TrajectoryStore } from './modules/tracking/TrajectoryStore';
import type { BodyNodeLink } from './types';

const WASM_BASE_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm';
const MEDIAPIPE_MODEL_URL = {
  light:
    'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task',
  heavy:
    'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/latest/pose_landmarker_heavy.task',
} as const;

const INITIAL_SETTINGS: Settings = {
  appMode: 'pose',
  mirror: false,
  showLabels: false,
  showAngles: true,
  colorizeByScore: true,
  minScore: 0.5,
  inferEveryNthFrame: 1,
  numPoses: 1,
  primaryIndex: 0,
  showBoxes: true,
  showTrails: true,
  showGhosts: true,
  showTeams: true,
  trailLength: 90,
  ghostMaxAgeMs: 3000,
  reidLifetimeMs: 15000,
  showHeatmap: false,
  heatmapOpacity: 0.55,
  heatmapOnCourt: false,
  useDeepReid: false,
  useWorker: false,
  smoothPose: true,
  poseModelQuality: 'heavy',
  useCropPipeline: false,
  detectorKind: 'coco',
  deepReidKind: 'mediapipe',
  filterBackend: 'alphabeta',
  showEdges: false,
  bundleOnRecord: false,
  personsOnly: true,
};

const YOLO_MODEL_URL =
  'https://cdn.jsdelivr.net/gh/Hyuto/yolov8-onnxruntime-web@master/public/model/yolov8n.onnx';
const OSNET_MODEL_URL: string | undefined = undefined;

function buildDetector(kind: 'coco' | 'yolo', useWorker: boolean): Detector {
  if (kind === 'yolo') {
    return new YoloDetector({ modelUrl: YOLO_MODEL_URL, minScore: 0.3, maxDetections: 30 });
  }
  if (useWorker) return new CocoSsdDetectorWorker({ minScore: 0.4, maxDetections: 30 });
  return new CocoSsdDetector({ minScore: 0.4, maxDetections: 30 });
}

const DEEP_REID_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/image_embedder/mobilenet_v3_small/float32/1/mobilenet_v3_small.tflite';
const REID_THRESHOLD_SHALLOW = 0.35;
const REID_THRESHOLD_DEEP = 0.3;

type SourceMode = 'camera' | 'file' | 'screen';
type EngineKey = 'mediapipe' | 'movenet' | 'rtmpose-t' | 'rtmpose-m' | 'rtmpose-l';

type PoseQuality = 'light' | 'heavy';

function buildEngine(
  key: EngineKey,
  numPoses: number,
  useWorker: boolean,
  quality: PoseQuality,
): PoseEngine {
  if (key === 'movenet') {
    const modelType = quality === 'heavy' ? 'thunder' : 'lightning';
    return useWorker
      ? new MoveNetPoseEngineWorker({ modelType })
      : new MoveNetPoseEngine({ modelType });
  }
  if (key === 'rtmpose-t' || key === 'rtmpose-m' || key === 'rtmpose-l') {
    const tier = key.slice('rtmpose-'.length) as 't' | 'm' | 'l';
    return new RTMPoseEngine({ tier });
  }
  const opts = {
    wasmBaseUrl: WASM_BASE_URL,
    modelAssetPath: MEDIAPIPE_MODEL_URL[quality],
    numPoses,
  };
  return useWorker ? new MediaPipePoseEngineWorker(opts) : new MediaPipePoseEngine(opts);
}

function profileEnabledFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get('profile') === '1';
}

/** Canonical save is on by default; only `?canonical=0` disables. The
 *  ONNX-backed lifter / depth passes stay opt-in so they don't bite
 *  users without self-hosted models. */
function canonicalSaveEnabledFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get('canonical') !== '0';
}

function lifterEnabledFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get('lifter') === '1';
}

function depthEnabledFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get('depth') === '1';
}

/** Module-level hook so call sites inside main() can register a HUD
 *  refresh that the file-scope tryBuildCanonicalBin() invokes after
 *  any ORT session is created. Set once during main() initialisation. */
let onOnnxEpMaybeChanged: (() => void) | null = null;

/** Best-effort: build the v3.2 canonical binary alongside the legacy
 *  PoseBinary. Returns null on any failure so the legacy save still
 *  succeeds. */
async function tryBuildCanonicalBin(
  poseBin: Uint8Array,
  representativeImage: HTMLVideoElement | HTMLCanvasElement | null,
): Promise<Uint8Array | null> {
  if (!canonicalSaveEnabledFromUrl()) return null;
  try {
    const lifter = lifterEnabledFromUrl() ? pickPoseLifter() : null;
    const depth = depthEnabledFromUrl() ? pickDepthEstimator() : null;
    // Snapshot the device gravity at save time. When the user has
    // granted DeviceOrientation permission this rotates MediaPipe's
    // world coords from camera-pitch-relative to gravity-relative,
    // removing the systematic ROM-angle bias that comes from holding
    // the camera tilted forward / sideways. No-op when no reading is
    // available (permission not yet granted, or desktop without a
    // motion sensor).
    const gravityReading = getGravityReading();
    const gravityRotation = gravityReading ? buildGravityRotation() : null;
    const result = await buildCanonicalBinFromPoseBin({
      poseBin,
      deviceIdHash: 'pc-host',
      lifter,
      depth,
      representativeImage,
      gravityRotation,
    });
    if (result.diagnostics.errors.length > 0) {
      console.warn('[canonical-save] diagnostics:', result.diagnostics);
    }
    // The lifter / depth passes may have triggered the first ORT
    // session of the page. Notify the host so the HUD badge updates.
    onOnnxEpMaybeChanged?.();
    return result.bytes;
  } catch (err) {
    console.warn('[canonical-save] failed, continuing with legacy-only:', err);
    return null;
  }
}

function buildViewerUrl(id: string): string {
  const url = new URL('./viewer.html', window.location.href);
  url.searchParams.set('id', id);
  if (profileEnabledFromUrl()) {
    url.searchParams.set('profile', '1');
  }
  return url.toString();
}

async function main(): Promise<void> {
  const video = document.getElementById('video') as HTMLVideoElement;
  const canvas = document.getElementById('overlay') as HTMLCanvasElement;
  const hudRoot = document.getElementById('hud')!;

  const fileInput = document.getElementById('file-input') as HTMLInputElement;
  const fileName = document.getElementById('file-name') as HTMLElement;
  const filePanel = document.getElementById('file-panel') as HTMLElement;
  const filePlayBtn = document.getElementById('file-toggle-play') as HTMLButtonElement;
  const fileOpenViewerBtn = document.getElementById('file-open-viewer') as HTMLButtonElement | null;
  const fileTime = document.getElementById('file-time') as HTMLElement;
  const fileSeek = document.getElementById('file-seek') as HTMLInputElement;

  const screenPanel = document.getElementById('screen-panel') as HTMLElement;
  const ytUrlInput = document.getElementById('yt-url') as HTMLInputElement;
  const ytHint = document.getElementById('yt-hint') as HTMLElement;
  const ytOpenBtn = document.getElementById('yt-open') as HTMLButtonElement;
  const ytUseEmbed = document.getElementById('yt-use-embed') as HTMLInputElement;
  const screenCaptureBtn = document.getElementById('screen-capture') as HTMLButtonElement;
  const screenStopBtn = document.getElementById('screen-stop') as HTMLButtonElement;

  const exportJsonBtn = document.getElementById('export-json') as HTMLButtonElement;
  const exportCsvBtn = document.getElementById('export-csv') as HTMLButtonElement;

  const courtCanvas = document.getElementById('court-canvas') as HTMLCanvasElement;
  const courtCalibrateBtn = document.getElementById('court-calibrate') as HTMLButtonElement;
  const courtClearBtn = document.getElementById('court-clear') as HTMLButtonElement;
  const courtHint = document.getElementById('court-hint') as HTMLElement;
  const stageEl = document.querySelector('.stage') as HTMLElement;

  const composeCanvas = document.getElementById('compose-canvas') as HTMLCanvasElement;
  const composeCtx = composeCanvas.getContext('2d')!;
  const recordToggleBtn = document.getElementById('record-toggle') as HTMLButtonElement;
  const recordTime = document.getElementById('record-time') as HTMLElement;
  const pipToggleBtn = document.getElementById('pip-toggle') as HTMLButtonElement | null;
  const profileToggle = document.getElementById('toggle-profile') as HTMLInputElement | null;
  const profileSummary = document.getElementById('hud-profile-summary') as HTMLElement | null;
  const profileLogBtn = document.getElementById('profile-log') as HTMLButtonElement | null;
  const profileSaveBtn = document.getElementById('profile-save') as HTMLButtonElement | null;
  const profileResetBtn = document.getElementById('profile-reset') as HTMLButtonElement | null;
  const pickSaveFolderBtn = document.getElementById('pick-save-folder') as HTMLButtonElement | null;
  const saveFolderStatus = document.getElementById('save-folder-status') as HTMLElement | null;
  recordToggleBtn.disabled = true;
  recordToggleBtn.title = 'Recording enabled once the pose model finishes loading';

  const sourceRadios = document.querySelectorAll<HTMLInputElement>('input[name="source"]');
  const engineRadios = document.querySelectorAll<HTMLInputElement>('input[name="engine"]');
  const onnxEpBadge = document.getElementById('onnx-ep-badge') as HTMLElement | null;
  const onnxEpBadgeName = document.getElementById('onnx-ep-badge-name') as HTMLElement | null;
  function refreshOnnxEpBadge(): void {
    if (!onnxEpBadge || !onnxEpBadgeName) return;
    const ep = lastPickedOnnxEP();
    if (!ep) {
      onnxEpBadge.hidden = true;
      return;
    }
    onnxEpBadge.hidden = false;
    onnxEpBadgeName.textContent = ep;
    // Subtle accent on WebGPU so users notice when accel is active.
    onnxEpBadgeName.style.color = ep === 'webgpu' ? 'var(--accent)' : '';
  }
  // Let the file-scope tryBuildCanonicalBin() (which runs after any
  // lifter / depth ORT session) refresh the badge.
  onOnnxEpMaybeChanged = refreshOnnxEpBadge;

  let engine: PoseEngine | null = buildEngine(
    'mediapipe',
    INITIAL_SETTINGS.numPoses,
    INITIAL_SETTINGS.useWorker,
    INITIAL_SETTINGS.poseModelQuality,
  );
  const profiler = new RuntimeProfiler({ enabled: profileEnabledFromUrl() });
  (
    window as Window & {
      __bnlProfiler?: RuntimeProfiler;
    }
  ).__bnlProfiler = profiler;
  const hud = new HudController(hudRoot, engine.skeleton, INITIAL_SETTINGS);
  const renderer = new Renderer(canvas);
  const trackingRenderer = new TrackingRenderer(canvas);
  const tracker = new Tracker({
    iouThreshold: 0.3,
    maxAgeMs: INITIAL_SETTINGS.ghostMaxAgeMs,
    reidLifetimeMs: INITIAL_SETTINGS.reidLifetimeMs,
    filterBackend: INITIAL_SETTINGS.filterBackend,
  });
  const edgeDetector = new EdgeDetector({ threshold: 60 });
  const trackTags = new TrackTagStore();
  const edgeScratch = document.createElement('canvas');
  edgeScratch.width = edgeDetector.width();
  edgeScratch.height = edgeDetector.height();
  const edgeScratchCtx = edgeScratch.getContext('2d');
  const trajectories = new TrajectoryStore({ maxPoints: INITIAL_SETTINGS.trailLength });
  const teamClassifier = new TeamClassifier();
  const colorSampleCanvas = document.createElement('canvas');
  const reidSampleCanvas = document.createElement('canvas');
  const heatmapRenderer = new HeatmapRenderer({
    opacity: INITIAL_SETTINGS.heatmapOpacity,
  });
  const poseRecordStore = new PoseRecordStore({ maxRecords: 600 });
  const poseSmoother = new PoseSmoother({ mincutoff: 1, beta: 0.08 });
  let recordingStartedAt = 0;
  let lastProfileSummaryAt = 0;
  let currentFileBlob: File | null = null;
  let lastFilePoseTimeMs = -1;
  let isSavingFileViewer = false;
  const poseRecSlider = document.getElementById('slider-poserec') as HTMLInputElement | null;
  const poseRecVal = document.getElementById('slider-poserec-val') as HTMLElement | null;
  if (poseRecSlider && poseRecVal) {
    poseRecSlider.addEventListener('input', () => {
      const v = Math.max(60, Math.min(1800, Math.floor(Number(poseRecSlider.value))));
      poseRecVal.textContent = String(v);
      poseRecordStore.setMaxRecords(v);
    });
  }
  const courtCalibrator = new CourtCalibrator(BASKETBALL_COURT);
  const courtView = new CourtView(courtCanvas, {
    widthMeters: BASKETBALL_COURT.widthMeters,
    heightMeters: BASKETBALL_COURT.heightMeters,
  });
  let calibrating = false;

  function refreshCourtHint(): void {
    const h = courtCalibrator.getHomography();
    if (calibrating) {
      const label = courtCalibrator.nextLabel();
      courtHint.textContent = label
        ? `Pick ${label} (${courtCalibrator.progress()}/4)…`
        : 'Calibrating…';
    } else if (h) {
      courtHint.textContent = 'Court calibrated. Stats project to court coordinates.';
    } else {
      courtHint.textContent =
        'Click 4 corners on the video: top-left → top-right → bottom-right → bottom-left.';
    }
    courtClearBtn.disabled = courtCalibrator.progress() === 0 && h === null;
    courtCalibrateBtn.textContent = calibrating ? 'Cancel' : h ? 'Recalibrate' : 'Calibrate';
    stageEl.classList.toggle('calibrating', calibrating);
  }
  function buildDeepReidExtractor(
    kind: 'mediapipe' | 'onnx',
    useWorker: boolean,
  ): DeepReIdExtractorLike {
    if (kind === 'onnx' && OSNET_MODEL_URL) {
      return new OnnxReidExtractor({ modelUrl: OSNET_MODEL_URL });
    }
    const o = { wasmBaseUrl: WASM_BASE_URL, modelAssetPath: DEEP_REID_MODEL_URL };
    return useWorker ? new DeepReIdExtractorWorker(o) : new DeepReIdExtractor(o);
  }

  let deepReidExtractor: DeepReIdExtractorLike = buildDeepReidExtractor(
    INITIAL_SETTINGS.deepReidKind,
    INITIAL_SETTINGS.useWorker,
  );
  const camera = new CameraManager(video);
  const fileSource = new FileSource(video);
  const screenSource = new ScreenSource(video);
  const perf = new PerformanceController({ inferEveryNthFrame: 1 });
  // fps is an upper cap on the captureStream sample rate. Actual output fps
  // follows the rate at which we update composeCanvas via rVFC.
  const recorder = new Recorder(composeCanvas, { fps: 60, bitsPerSecond: 6_000_000 });
  const wakeLock = new WakeLockGuard();
  const keepTabActive = new KeepTabActive();
  const fileSaver = new FileSystemSaver();

  let detector: Detector | null = null;
  let cropDetector: Detector | null = null;
  let cropOrchestrator: CroppedPoseOrchestrator | null = null;
  let cropReady = false;
  let resolution = { width: 1280, height: 720 };
  let currentMode: SourceMode = 'camera';
  let currentEngineKey: EngineKey = 'mediapipe';
  let currentAppMode: AppMode = INITIAL_SETTINGS.appMode;
  let latest: BodyNodeLink[] = [];
  let lastInferenceMs = 0;
  let isInferring = false;
  let isSwitching = false;
  let appliedNumPoses = INITIAL_SETTINGS.numPoses;
  let appliedTrailLength = INITIAL_SETTINGS.trailLength;
  let appliedGhostMaxAge = INITIAL_SETTINGS.ghostMaxAgeMs;
  let appliedReidLifetime = INITIAL_SETTINGS.reidLifetimeMs;
  let appliedHeatmapOpacity = INITIAL_SETTINGS.heatmapOpacity;
  let appliedUseDeepReid = false;
  let appliedUseWorker = INITIAL_SETTINGS.useWorker;
  let appliedPoseQuality = INITIAL_SETTINGS.poseModelQuality;
  let appliedCropPipeline = INITIAL_SETTINGS.useCropPipeline;
  let appliedDetectorKind = INITIAL_SETTINGS.detectorKind;
  let appliedDeepReidKind = INITIAL_SETTINGS.deepReidKind;
  let appliedFilterBackend = INITIAL_SETTINGS.filterBackend;

  function refreshCropOrchestrator(): void {
    if (!cropReady || !cropDetector || !engine) {
      cropOrchestrator = null;
      return;
    }
    cropOrchestrator = new CroppedPoseOrchestrator(cropDetector, engine, {
      padding: 0.18,
      maxPeople: Math.max(1, hud.getSettings().numPoses),
    });
  }

  /** Detector kind currently powering the crop pipeline. Tracked so a
   *  HUD setting flip rebuilds the pipeline with the right model. */
  let cropDetectorKind: 'coco' | 'yolo' | null = null;

  async function ensureCropPipeline(): Promise<void> {
    const wantedKind = hud.getSettings().detectorKind;
    if (cropReady && cropDetectorKind === wantedKind) return;
    if (cropReady && cropDetector) {
      // Tear down the previous detector before swapping in the new
      // one so YOLO ↔ COCO flips don't leak ONNX / TFJS sessions.
      try {
        (cropDetector as Detector & { dispose?: () => void }).dispose?.();
      } catch {
        /* ignore */
      }
    }
    hud.setStatus(
      `loading ${wantedKind === 'yolo' ? 'YOLOv8n' : 'COCO-SSD'} detector for crop pipeline…`,
    );
    // Crop pipeline always runs the detector on the *main* video
    // surface, so a worker variant adds an OffscreenCanvas hop with
    // no parallelism win — keep both kinds main-thread.
    cropDetector =
      wantedKind === 'yolo'
        ? new YoloDetector({ modelUrl: YOLO_MODEL_URL, minScore: 0.3, maxDetections: 30 })
        : new CocoSsdDetector({ minScore: 0.35, maxDetections: 10 });
    await cropDetector.initialize();
    cropReady = true;
    cropDetectorKind = wantedKind;
    refreshCropOrchestrator();
    hud.setStatus('running');
  }
  let deepReidInitializing = false;
  let framesSinceClassify = 0;
  let framesSinceHeatmap = 0;
  const frameTimestamps: number[] = [];

  hud.onSettingsChange((s) => {
    perf.setSkip(s.inferEveryNthFrame);
    applyMirror(video, s.mirror);
    if (s.numPoses !== appliedNumPoses && engine) {
      appliedNumPoses = s.numPoses;
      void engine.setNumPoses(s.numPoses).catch((err) => {
        console.error('[BodyNodeLink] setNumPoses', err);
      });
    }
    if (s.trailLength !== appliedTrailLength) {
      appliedTrailLength = s.trailLength;
      trajectories.setMaxPoints(s.trailLength);
    }
    if (s.ghostMaxAgeMs !== appliedGhostMaxAge) {
      appliedGhostMaxAge = s.ghostMaxAgeMs;
      tracker.setMaxAgeMs(s.ghostMaxAgeMs);
    }
    if (s.reidLifetimeMs !== appliedReidLifetime) {
      appliedReidLifetime = s.reidLifetimeMs;
      tracker.setReidLifetimeMs(s.reidLifetimeMs);
    }
    if (s.heatmapOpacity !== appliedHeatmapOpacity) {
      appliedHeatmapOpacity = s.heatmapOpacity;
      heatmapRenderer.setOpacity(s.heatmapOpacity);
    }
    if (s.poseModelQuality !== appliedPoseQuality) {
      appliedPoseQuality = s.poseModelQuality;
      if (!isSwitching && currentAppMode === 'pose') {
        void swapEngine(s.useWorker);
      }
    }
    if (s.useCropPipeline !== appliedCropPipeline) {
      appliedCropPipeline = s.useCropPipeline;
      if (s.useCropPipeline) {
        void ensureCropPipeline();
      } else {
        cropOrchestrator = null;
      }
    }
    if (s.useWorker !== appliedUseWorker) {
      appliedUseWorker = s.useWorker;
      deepReidExtractor.dispose();
      deepReidExtractor = buildDeepReidExtractor(s.deepReidKind, s.useWorker);
      if (s.useDeepReid) {
        deepReidInitializing = true;
        hud.setReidMode('loading deep');
        deepReidExtractor
          .initialize()
          .then(() => hud.setReidMode('deep'))
          .catch((err) => {
            console.error('[BodyNodeLink] deep re-id re-init', err);
            hud.setReidMode('shallow');
          })
          .finally(() => {
            deepReidInitializing = false;
          });
      }
      if (!isSwitching) {
        if (currentAppMode === 'tracking') void swapDetector(s.useWorker);
        else if (currentAppMode === 'pose') void swapEngine(s.useWorker);
      }
    }
    if (s.detectorKind !== appliedDetectorKind) {
      appliedDetectorKind = s.detectorKind;
      if (!isSwitching && currentAppMode === 'tracking') {
        void swapDetector(s.useWorker);
      }
      // Crop pipeline previously used a COCO-SSD instance unconditionally;
      // mirror tracking's detectorKind here so toggling YOLO in the HUD
      // actually changes which model crops the input frame.
      if (s.useCropPipeline) {
        void ensureCropPipeline();
      }
    }
    if (s.deepReidKind !== appliedDeepReidKind) {
      appliedDeepReidKind = s.deepReidKind;
      deepReidExtractor.dispose();
      deepReidExtractor = buildDeepReidExtractor(s.deepReidKind, s.useWorker);
      if (s.useDeepReid) {
        deepReidInitializing = true;
        hud.setReidMode('loading deep');
        deepReidExtractor
          .initialize()
          .then(() => hud.setReidMode('deep'))
          .catch((err) => {
            console.error('[BodyNodeLink] deep re-id re-init', err);
            hud.setReidMode('shallow');
          })
          .finally(() => {
            deepReidInitializing = false;
          });
      }
    }
    if (s.filterBackend !== appliedFilterBackend) {
      appliedFilterBackend = s.filterBackend;
      tracker.setFilterBackend(s.filterBackend);
    }
    if (s.useDeepReid !== appliedUseDeepReid) {
      appliedUseDeepReid = s.useDeepReid;
      tracker.setReidThreshold(s.useDeepReid ? REID_THRESHOLD_DEEP : REID_THRESHOLD_SHALLOW);
      tracker.reset();
      trajectories.reset();
      trackTags.clear();
      teamClassifier.reset();
      resetPoseAnalysisState();
      if (s.useDeepReid && !deepReidExtractor.isReady() && !deepReidInitializing) {
        deepReidInitializing = true;
        hud.setReidMode('loading deep');
        deepReidExtractor
          .initialize()
          .then(() => {
            hud.setReidMode('deep');
          })
          .catch((err) => {
            console.error('[BodyNodeLink] deep re-id init', err);
            hud.setReidMode('shallow');
          })
          .finally(() => {
            deepReidInitializing = false;
          });
      } else {
        hud.setReidMode(s.useDeepReid ? 'deep' : 'shallow');
      }
    }
    if (s.appMode !== currentAppMode) {
      void switchAppMode(s.appMode);
    }
  });

  function selectSourceRadio(mode: SourceMode): void {
    const radio = document.querySelector<HTMLInputElement>(`input[name="source"][value="${mode}"]`);
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  async function switchEngine(key: EngineKey): Promise<void> {
    if (key === currentEngineKey || isSwitching) return;
    if (currentAppMode !== 'pose') {
      currentEngineKey = key;
      return;
    }
    isSwitching = true;
    try {
      hud.setStatus(`loading ${key}…`);
      engine?.dispose();
      latest = [];
      renderer.clear();
      const settings = hud.getSettings();
      engine = buildEngine(key, settings.numPoses, settings.useWorker, settings.poseModelQuality);
      await engine.initialize();
      refreshCropOrchestrator();
      refreshOnnxEpBadge();
      currentEngineKey = key;
      hud.rebuildSkeleton(engine.skeleton);
      hud.setMultiPoseEnabled(engine.supportsMultiPose);
      appliedNumPoses = engine.supportsMultiPose ? settings.numPoses : 1;
      hud.setStatus('running');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`engine error: ${message}`);
      console.error('[BodyNodeLink] switchEngine', err);
    } finally {
      isSwitching = false;
    }
  }

  async function swapEngine(useWorker: boolean): Promise<void> {
    if (currentAppMode !== 'pose') return;
    isSwitching = true;
    try {
      hud.setStatus(`loading engine (${useWorker ? 'worker' : 'main'})…`);
      engine?.dispose();
      engine = null;
      latest = [];
      renderer.clear();
      const settings = hud.getSettings();
      engine = buildEngine(
        currentEngineKey,
        settings.numPoses,
        useWorker,
        settings.poseModelQuality,
      );
      await engine.initialize();
      refreshCropOrchestrator();
      refreshOnnxEpBadge();
      hud.rebuildSkeleton(engine.skeleton);
      hud.setMultiPoseEnabled(engine.supportsMultiPose);
      appliedNumPoses = engine.supportsMultiPose ? settings.numPoses : 1;
      hud.setStatus('running');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`engine error: ${message}`);
      console.error('[BodyNodeLink] swapEngine', err);
    } finally {
      isSwitching = false;
    }
  }

  async function swapDetector(useWorker: boolean): Promise<void> {
    if (currentAppMode !== 'tracking') return;
    isSwitching = true;
    try {
      hud.setStatus(`loading detector (${useWorker ? 'worker' : 'main'})…`);
      detector?.dispose();
      detector = null;
      latest = [];
      tracker.reset();
      trajectories.reset();
      trackTags.clear();
      teamClassifier.reset();
      resetPoseAnalysisState();
      detector = buildDetector(hud.getSettings().detectorKind, useWorker);
      await detector.initialize();
      hud.setStatus('running');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`detector error: ${message}`);
      console.error('[BodyNodeLink] swapDetector', err);
    } finally {
      isSwitching = false;
    }
  }

  async function switchAppMode(mode: AppMode): Promise<void> {
    if (mode === currentAppMode || isSwitching) return;
    isSwitching = true;
    try {
      hud.setStatus(`switching to ${mode}…`);
      latest = [];
      renderer.clear();
      trackingRenderer.clear();
      tracker.reset();
      trajectories.reset();
      trackTags.clear();
      teamClassifier.reset();
      resetPoseAnalysisState();
      framesSinceClassify = 0;

      if (currentAppMode === 'pose') {
        engine?.dispose();
        engine = null;
      } else {
        detector?.dispose();
        detector = null;
      }

      currentAppMode = mode;

      if (mode === 'pose') {
        const settings = hud.getSettings();
        engine = buildEngine(
          currentEngineKey,
          settings.numPoses,
          settings.useWorker,
          settings.poseModelQuality,
        );
        await engine.initialize();
        refreshCropOrchestrator();
        refreshOnnxEpBadge();
        hud.rebuildSkeleton(engine.skeleton);
        hud.setMultiPoseEnabled(engine.supportsMultiPose);
        appliedNumPoses = engine.supportsMultiPose ? settings.numPoses : 1;
      } else {
        const s = hud.getSettings();
        detector = buildDetector(s.detectorKind, s.useWorker);
        await detector.initialize();
      }

      hud.setStatus('running');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`mode error: ${message}`);
      console.error('[BodyNodeLink] switchAppMode', err);
    } finally {
      refreshFileViewerButton();
      isSwitching = false;
    }
  }

  for (const radio of engineRadios) {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      void switchEngine(radio.value as EngineKey);
    });
  }

  async function switchMode(mode: SourceMode): Promise<void> {
    console.info(
      '[switchMode] called with',
      mode,
      'currentMode=',
      currentMode,
      'isSwitching=',
      isSwitching,
    );
    if (mode === currentMode || isSwitching) {
      console.warn('[switchMode] EARLY EXIT — no-op');
      return;
    }
    isSwitching = true;
    try {
      currentMode = mode;
      console.info('[switchMode] set currentMode=', mode);
      latest = [];
      renderer.clear();
      // Wipe the composeCanvas so the next recorder.start() doesn't pick
      // up a frozen frame from the previous source if rVFC is briefly
      // late on the new source's first decoded frame.
      try {
        composeCtx.clearRect(0, 0, composeCanvas.width, composeCanvas.height);
      } catch {
        /* compose ctx may not be ready during early init */
      }

      filePanel.hidden = true;
      screenPanel.hidden = true;

      if (mode !== 'camera') camera.stop();
      if (mode !== 'file') {
        fileSource.stop();
        currentFileBlob = null;
        lastFilePoseTimeMs = -1;
        refreshFileViewerButton();
        refreshFilePanelState();
      }
      if (mode !== 'screen') screenSource.stop();

      // Sync the Source radio to reflect the new mode without dispatching
      // a `change` event (which would re-enter switchMode). Action
      // buttons (Record, Capture) call switchMode directly, so we need
      // the radios to follow along visually.
      const targetRadio = document.querySelector<HTMLInputElement>(
        `input[name="source"][value="${mode}"]`,
      );
      if (targetRadio && !targetRadio.checked) targetRadio.checked = true;

      if (mode === 'camera') {
        hud.setStatus('requesting camera…');
        const res = await camera.start({
          width: 1280,
          height: 720,
          facingMode: 'user',
        });
        resolution = res;
        renderer.resize(res.width, res.height);
        hud.setStatus('running');
      } else if (mode === 'file') {
        filePanel.hidden = false;
        filePlayBtn.disabled = true;
        fileSeek.disabled = true;
        refreshFileViewerButton();
        hud.setStatus('waiting for file…');
      } else {
        screenPanel.hidden = false;
        screenStopBtn.disabled = true;
        refreshYtOpenButton();
        ytHint.textContent =
          '1) Open YouTube in a new tab · 2) Come back here · 3) Click Capture and pick that tab.';
        hud.setStatus('waiting for capture…');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`error: ${message}`);
      console.error('[BodyNodeLink] switchMode', err);
    } finally {
      refreshFileViewerButton();
      isSwitching = false;
    }
  }

  for (const radio of sourceRadios) {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      void switchMode(radio.value as SourceMode);
    });
  }

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    console.info('[fileInput#change] got file=', !!file, 'currentMode=', currentMode);
    if (!file) return;
    // Ensure we're in file mode. On some browsers the radio `change` event
    // doesn't fire reliably when the file picker also opens (notably iOS/
    // Safari when a label-wrapped radio loses focus to the picker); the
    // File UI can end up visible while `currentMode` is still 'camera'.
    if (currentMode !== 'file') {
      console.warn('[fileInput#change] currentMode is not file — forcing switchMode');
      await switchMode('file');
    }
    fileName.textContent = file.name;
    hud.setStatus('loading file…');
    resetPoseAnalysisState();
    // Drop any frozen frame from the previous file / source so the next
    // recording can't pick it up.
    try {
      composeCtx.clearRect(0, 0, composeCanvas.width, composeCanvas.height);
    } catch {
      /* compose ctx may not be ready during early init */
    }
    currentFileBlob = file;
    refreshFileViewerButton();
    refreshFilePanelState();
    try {
      const res = await fileSource.start(file);
      resolution = { width: res.width, height: res.height };
      renderer.resize(res.width, res.height);
      filePlayBtn.disabled = false;
      fileSeek.disabled = false;
      updateFileUi();
      refreshFileViewerButton();
      hud.setStatus('running file analysis…');
    } catch (err) {
      currentFileBlob = null;
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`error: ${message}`);
      console.error('[BodyNodeLink] file open', err);
      refreshFileViewerButton();
      refreshFilePanelState();
    }
  });

  filePlayBtn.addEventListener('click', () => {
    if (currentMode !== 'file') return;
    if (video.paused) {
      void (async () => {
        const dur = Number.isFinite(video.duration) ? video.duration : 0;
        if (video.ended || (dur > 0 && video.currentTime >= dur - 0.05)) {
          resetPoseAnalysisState();
          await seekVideo(video, 0);
        }
        try {
          await video.play();
        } catch (err) {
          console.error('[BodyNodeLink] file play', err);
          hud.setStatus(`file play failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      })();
    } else {
      video.pause();
    }
  });

  fileOpenViewerBtn?.addEventListener('click', () => {
    void openViewerForCurrentFile();
  });

  fileSeek.addEventListener('input', () => {
    if (currentMode !== 'file') return;
    const dur = video.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    const nextTime = Number(fileSeek.value) * dur;
    const currentTime = video.currentTime;
    const seekingBackward = nextTime + 0.05 < currentTime;
    video.currentTime = nextTime;
    if (seekingBackward) {
      resetPoseAnalysisState();
      hud.setStatus('file seeked backward — restarted pose capture for viewer');
    }
  });

  video.addEventListener('play', () => {
    filePlayBtn.textContent = 'Pause';
  });
  video.addEventListener('pause', () => {
    filePlayBtn.textContent = 'Play';
  });
  video.addEventListener('ended', () => {
    if (currentMode !== 'file') return;
    filePlayBtn.textContent = 'Replay';
    refreshFileViewerButton();
    if (currentAppMode === 'pose' && currentFileBlob && poseRecordStore.count() > 0) {
      hud.setStatus('analysis pass complete — open viewer to inspect');
    }
  });
  video.addEventListener('timeupdate', updateFileUi);
  video.addEventListener('loadedmetadata', updateFileUi);
  video.addEventListener('durationchange', updateFileUi);
  video.addEventListener('seeked', updateFileUi);

  video.addEventListener('resize', () => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w <= 0 || h <= 0) return;
    if (resolution.width === w && resolution.height === h) return;
    resolution = { width: w, height: h };
    renderer.resize(w, h);
  });

  let updateFileUiCount = 0;
  function updateFileUi(): void {
    updateFileUiCount += 1;
    const mode = currentMode;
    const cur = video.currentTime || 0;
    const dur = Number.isFinite(video.duration) ? video.duration : 0;
    if (updateFileUiCount <= 3 || updateFileUiCount % 30 === 0) {
      console.info(
        '[updateFileUi#' + updateFileUiCount + '] mode=',
        mode,
        'cur=',
        cur.toFixed(3),
        'dur=',
        dur.toFixed(3),
        'fileTimeEl.id=',
        fileTime?.id,
        'fileTimeText=',
        fileTime?.textContent,
      );
    }
    if (mode !== 'file') return;
    fileTime.textContent = `${formatTime(cur)} / ${formatTime(dur)}`;
    if (dur > 0 && document.activeElement !== fileSeek) {
      fileSeek.value = String(cur / dur);
    }
  }

  // Dump HUD file-panel state once per second so we can see what the user is
  // actually looking at vs internal state.
  setInterval(() => {
    if (currentMode !== 'file') return;
    console.info(
      '[HUD-check] mode=',
      currentMode,
      'playBtn.disabled=',
      filePlayBtn.disabled,
      'playBtn.text=',
      filePlayBtn.textContent,
      'fileTime.text=',
      fileTime.textContent,
      'openViewerBtn.disabled=',
      fileOpenViewerBtn?.disabled,
      'video.paused=',
      video.paused,
      'video.currentTime=',
      video.currentTime.toFixed(3),
      'video.duration=',
      String(video.duration),
    );
  }, 2000);

  function refreshYtOpenButton(): void {
    const normalized = normalizeYouTubeUrl(ytUrlInput.value);
    ytOpenBtn.disabled = normalized === null;
  }

  ytUrlInput.addEventListener('input', refreshYtOpenButton);

  ytOpenBtn.addEventListener('click', () => {
    const target = ytUseEmbed.checked
      ? toYouTubeEmbedUrl(ytUrlInput.value)
      : normalizeYouTubeUrl(ytUrlInput.value);
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
    ytHint.textContent = ytUseEmbed.checked
      ? 'Embed tab opened. If you see "Error 153", the video disallows embedding — uncheck embed and retry.'
      : 'YouTube tab opened. Press "t" for Theater mode (or fullscreen) to hide thumbnails, then come back and Capture.';
  });

  screenCaptureBtn.addEventListener('click', async () => {
    // Capture button itself selects the screen source — users don't
    // need to pre-click the Source tab. Matches the "File or Capture
    // determines the target" UX model.
    if (currentMode !== 'screen') {
      await switchMode('screen');
      if ((currentMode as SourceMode) !== 'screen') return;
    }
    hud.setStatus('requesting screen share…');
    try {
      const res = await screenSource.start({
        onShareEnded: () => {
          if (currentMode === 'screen') {
            hud.setStatus('share ended');
            selectSourceRadio('camera');
          }
        },
      });
      resolution = res;
      renderer.resize(res.width, res.height);
      screenStopBtn.disabled = false;
      applyMirror(video, false);
      hud.setStatus('running');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      hud.setStatus(`capture canceled: ${message}`);
      console.error('[BodyNodeLink] screen capture', err);
    }
  });

  courtCalibrateBtn.addEventListener('click', () => {
    if (calibrating) {
      calibrating = false;
      courtCalibrator.reset();
    } else {
      courtCalibrator.reset();
      calibrating = true;
    }
    refreshCourtHint();
  });

  courtClearBtn.addEventListener('click', () => {
    courtCalibrator.reset();
    calibrating = false;
    courtView.clear();
    refreshCourtHint();
  });

  const tagsClearBtn = document.getElementById('tags-clear') as HTMLButtonElement | null;
  tagsClearBtn?.addEventListener('click', () => {
    trackTags.clear();
  });

  stageEl.addEventListener('click', (ev) => {
    const rect = stageEl.getBoundingClientRect();
    const cx = (ev.clientX - rect.left) / rect.width;
    const cy = (ev.clientY - rect.top) / rect.height;
    if (calibrating) {
      courtCalibrator.pushPoint({ x: cx, y: cy });
      if (courtCalibrator.progress() >= 4) calibrating = false;
      refreshCourtHint();
      return;
    }
    if (currentAppMode !== 'tracking') return;
    const picked = pickPersonTrack(cx, cy, hud.getSettings().mirror);
    if (!picked) return;
    const current = trackTags.get(picked.id) ?? '';
    const entered = window.prompt(`Tag for P${picked.id}`, current);
    if (entered === null) return;
    trackTags.set(picked.id, entered);
  });

  function pickPersonTrack(nx: number, ny: number, mirror: boolean): TrackedObject | null {
    const tracks = tracker.getTracks();
    let best: TrackedObject | null = null;
    let bestArea = Infinity;
    const probeX = mirror ? 1 - nx : nx;
    for (const t of tracks) {
      if (t.class !== 'person') continue;
      if (t.isGhost) continue;
      const inside =
        probeX >= t.box.x &&
        probeX <= t.box.x + t.box.w &&
        ny >= t.box.y &&
        ny <= t.box.y + t.box.h;
      if (!inside) continue;
      const area = t.box.w * t.box.h;
      if (area < bestArea) {
        bestArea = area;
        best = t;
      }
    }
    return best;
  }

  refreshCourtHint();

  function trackingSummary(): { count: number; points: number } {
    const all = trajectories.getAll();
    let points = 0;
    for (const t of all) points += t.points.length;
    return { count: all.length, points };
  }

  function stamp(): string {
    return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  }

  function refreshFileViewerButton(): void {
    if (!fileOpenViewerBtn) return;
    fileOpenViewerBtn.disabled =
      isSavingFileViewer || currentMode !== 'file' || currentFileBlob === null;
  }

  function refreshFilePanelState(): void {
    // Two-state file panel: 'empty' (file picker prominent) vs
    // 'loaded' (play / pause / seek active). CSS hides the inactive
    // sub-panel via `[data-state="…"]` selectors.
    filePanel.dataset.state = currentFileBlob === null ? 'empty' : 'loaded';
  }

  function resetPoseCapture(): void {
    poseRecordStore.reset();
    lastFilePoseTimeMs = -1;
    refreshFileViewerButton();
  }

  function resetPoseAnalysisState(): void {
    resetPoseCapture();
    poseSmoother.reset();
    // Clear the last drawn skeleton so a new file / mode switch doesn't
    // show stale lines from the previous source until the next inference
    // result lands. Without this, loading a 2nd clip while the 1st clip's
    // pose was the most recent inference left the 1st clip's skeleton
    // stuck on screen — the user reads this as "1st video isn't being
    // replaced", because the most visible artifact (the skeleton) hasn't
    // changed.
    latest = [];
    try {
      renderer.clear();
    } catch {
      /* renderer may not be ready during early init */
    }
    hud.updateAngles({});
    // Wipe the per-node score bars by feeding a body with no nodes —
    // every bar resolves `score = 0`. Avoids the expensive
    // rebuildSkeleton() DOM teardown when only the values need clearing.
    hud.updateNodeScores({ nodes: [], links: [], fps: 0, inferenceTimeMs: 0 });
  }

  function refreshProfileSummary(): void {
    if (!profileSummary) return;
    const lines = profiler.summaryLines(6);
    const status = profiler.isEnabled() ? 'profiling on' : 'profiling paused';
    profileSummary.textContent = [status, ...lines].join('\n');
  }

  function saveProfileSnapshot(scope: string): void {
    const payload = {
      scope,
      page: 'app',
      href: window.location.href,
      userAgent: navigator.userAgent,
      snapshot: profiler.snapshot(),
    };
    downloadBlob(
      new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
      `bnl-profile-${scope}-${stamp()}.json`,
    );
  }

  if (profileToggle) {
    profileToggle.checked = profiler.isEnabled();
    profileToggle.addEventListener('change', () => {
      profiler.setEnabled(profileToggle.checked);
      hud.setStatus(profileToggle.checked ? 'profiling enabled' : 'profiling paused');
      refreshProfileSummary();
    });
  }
  profileLogBtn?.addEventListener('click', () => {
    console.table(profiler.snapshot().timings);
    console.table(profiler.snapshot().observations);
    hud.setStatus('profile snapshot logged to console');
  });
  profileSaveBtn?.addEventListener('click', () => {
    saveProfileSnapshot('app');
    hud.setStatus('profile snapshot downloaded');
  });
  profileResetBtn?.addEventListener('click', () => {
    profiler.reset();
    refreshProfileSummary();
    hud.setStatus('profile samples reset');
  });
  refreshProfileSummary();
  refreshFileViewerButton();

  async function openViewerForCurrentFile(): Promise<void> {
    if (currentMode !== 'file') return;
    if (currentAppMode !== 'pose') {
      hud.setStatus('uploaded video viewer is available in Pose mode only');
      return;
    }
    if (!currentFileBlob) {
      hud.setStatus('select a video file first');
      return;
    }
    const fileBlob = currentFileBlob;
    const poseRecordsAll = Array.from(poseRecordStore.getAll());
    if (poseRecordsAll.length === 0) {
      hud.setStatus('no pose data yet — play the file to collect poses first');
      return;
    }

    // Auth gate before opening the viewer tab — LINE Login redirects
    // the browser away, so we must not pop the viewer first or it
    // would orphan on the original origin.
    const signedIn = await requireSignIn(window.location.pathname);
    if (!signedIn) {
      isSavingFileViewer = false;
      refreshFileViewerButton();
      hud.setStatus('LINE ログインが必要です — リダイレクトします');
      return;
    }
    const recId = newRecordingId();
    const viewerUrl = buildViewerUrl(recId);
    isSavingFileViewer = true;
    refreshFileViewerButton();
    markRecordingSaving(recId, { expectedFrames: poseRecordsAll.length });
    const viewerWin = window.open(viewerUrl, '_blank');
    if (!viewerWin) {
      hud.setStatus(`popup blocked — open ${viewerUrl} manually (data saved)`);
    } else {
      hud.setStatus(`saving uploaded analysis (${poseRecordsAll.length} frames)…`);
    }

    try {
      const poseBin = await profiler.measureAsync('file.pose_bin_encode', () =>
        encodePoseBinary(poseRecordsAll, {
          engineId: engine?.id,
          engineLabel: engine?.label,
          source: currentMode,
          resolution,
          recordingStartedAt: 0,
          angleKeys: JOINT_DEFINITIONS.map((d) => d.key),
        }),
      );
      const canonicalBin = await profiler.measureAsync(
        'file.canonical_build',
        () => tryBuildCanonicalBin(poseBin, video),
      );
      await profiler.measureAsync('file.save_indexeddb', () =>
        saveRecording({
          id: recId,
          createdAt: Date.now(),
          videoBlob: fileBlob,
          videoMime: fileBlob.type || 'video/mp4',
          poseBin,
          canonicalBin,
          engineId: engine?.id,
          engineLabel: engine?.label,
          resolution,
        }),
      );
      markRecordingReady(recId, { frames: poseRecordsAll.length });
      void pruneOldRecordings(3);
      hud.setStatus(`viewer ready · ${poseRecordsAll.length} frames`);
      refreshProfileSummary();
    } catch (err) {
      console.error('[BodyNodeLink] save uploaded recording', err);
      markRecordingFailed(recId, err instanceof Error ? err.message : String(err));
      hud.setStatus(`save failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      isSavingFileViewer = false;
      refreshFileViewerButton();
    }
  }

  async function seekVideo(videoEl: HTMLVideoElement, timeSec: number): Promise<void> {
    const clamped =
      Number.isFinite(videoEl.duration) && videoEl.duration > 0
        ? Math.max(0, Math.min(videoEl.duration, timeSec))
        : Math.max(0, timeSec);
    if (Math.abs(videoEl.currentTime - clamped) < 0.01) return;
    await new Promise<void>((resolve) => {
      const done = (): void => {
        videoEl.removeEventListener('seeked', done);
        resolve();
      };
      videoEl.addEventListener('seeked', done, { once: true });
      videoEl.currentTime = clamped;
    });
  }

  exportJsonBtn.addEventListener('click', () => {
    if (currentAppMode === 'pose') {
      const records = poseRecordStore.getAll();
      if (records.length === 0) {
        hud.setStatus('no pose data yet — wait for detections in Pose mode');
        return;
      }
      profiler.observe('export.pose.frames', records.length);
      const json = profiler.measure('export.pose_json', () =>
        posesToJson(records, {
          engineId: engine?.id,
          engineLabel: engine?.label,
          source: currentMode,
          resolution,
        }),
      );
      downloadBlob(new Blob([json], { type: 'application/json' }), `pose-${stamp()}.json`);
      hud.setStatus(`exported ${records.length} pose frames (JSON)`);
      refreshProfileSummary();
      return;
    }
    const { count, points } = trackingSummary();
    if (points === 0) {
      hud.setStatus('no tracking data yet — switch to Tracking and wait for detections');
      return;
    }
    profiler.observe('export.tracking.points', points);
    const json = profiler.measure('export.tracking_json', () =>
      trajectoriesToJson(trajectories.getAll(), {
        source: currentMode,
        resolution,
      }),
    );
    downloadBlob(new Blob([json], { type: 'application/json' }), `tracking-${stamp()}.json`);
    hud.setStatus(`exported ${count} tracks · ${points} points (JSON)`);
    refreshProfileSummary();
  });

  exportCsvBtn.addEventListener('click', () => {
    if (currentAppMode === 'pose') {
      const records = poseRecordStore.getAll();
      if (records.length === 0) {
        hud.setStatus('no pose data yet — wait for detections in Pose mode');
        return;
      }
      profiler.observe('export.pose.frames', records.length);
      const s = stamp();
      const nodesCsv = profiler.measure('export.pose_nodes_csv', () => nodesToCsv(records));
      const anglesCsv = profiler.measure('export.pose_angles_csv', () => anglesToCsv(records));
      downloadBlob(new Blob([nodesCsv], { type: 'text/csv' }), `pose-nodes-${s}.csv`);
      downloadBlob(new Blob([anglesCsv], { type: 'text/csv' }), `pose-angles-${s}.csv`);
      hud.setStatus(`exported ${records.length} pose frames (nodes + angles CSV)`);
      refreshProfileSummary();
      return;
    }
    const { count, points } = trackingSummary();
    if (points === 0) {
      hud.setStatus('no tracking data yet — switch to Tracking and wait for detections');
      return;
    }
    profiler.observe('export.tracking.points', points);
    const csv = profiler.measure('export.tracking_csv', () =>
      trajectoriesToCsv(trajectories.getAll()),
    );
    downloadBlob(new Blob([csv], { type: 'text/csv' }), `tracking-${stamp()}.csv`);
    hud.setStatus(`exported ${count} tracks · ${points} points (CSV)`);
    refreshProfileSummary();
  });

  screenStopBtn.addEventListener('click', () => {
    screenSource.stop();
    screenStopBtn.disabled = true;
    latest = [];
    renderer.clear();
    hud.setStatus('waiting for capture…');
  });

  // File System Access: let the user pick a persistent "recordings folder"
  // once, then auto-save future recordings there. Fallback is a normal
  // download when unsupported or no folder is chosen.
  if (pickSaveFolderBtn && FileSystemSaver.isSupported()) {
    pickSaveFolderBtn.hidden = false;
    void fileSaver.hasUsableDirectory().then((ok) => {
      if (saveFolderStatus) saveFolderStatus.textContent = ok ? 'folder set ✓' : '';
    });
    pickSaveFolderBtn.addEventListener('click', async () => {
      const ok = await fileSaver.pickDirectory();
      if (saveFolderStatus) saveFolderStatus.textContent = ok ? 'folder set ✓' : 'not set';
    });
  }

  // Picture-in-Picture: show the PiP button only when the platform
  // supports it. The PiP source is the live video element.
  if (pipToggleBtn) {
    if (document.pictureInPictureEnabled) {
      pipToggleBtn.hidden = false;
      pipToggleBtn.addEventListener('click', async () => {
        try {
          if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
          } else {
            await video.requestPictureInPicture();
          }
        } catch (err) {
          console.warn('[BodyNodeLink] PiP', err);
        }
      });
      video.addEventListener('enterpictureinpicture', () => {
        pipToggleBtn.textContent = 'Exit PiP';
      });
      video.addEventListener('leavepictureinpicture', () => {
        pipToggleBtn.textContent = 'PiP';
      });
    }
  }

  recordToggleBtn.addEventListener('click', async () => {
    // First Record click is a guaranteed user gesture; piggy-back the
    // iOS-only DeviceOrientation permission request here so the
    // canonical save can later use the gravity anchor without a
    // separate UI prompt. No-op when permission is already settled.
    if (getGravityPermissionState() === 'unknown') {
      void requestGravityPermission();
    }
    if (recorder.isRecording()) {
      // Capture the user's stop instant *before* yielding to `await` below.
      // Pose records captured during the recording (t0 <= recordingEndedAt)
      // should survive the filter regardless of when their inference finishes.
      const recordingEndedAt = performance.now();
      // Open the viewer tab SYNCHRONOUSLY, inside the user gesture, so the
      // popup blocker doesn't kill it. Any `await` before `window.open`
      // breaks the gesture chain on Safari / Chrome. The viewer polls IDB
      // until our save completes below.
      const pendingRecId = newRecordingId();
      const pendingViewerUrl = buildViewerUrl(pendingRecId);
      const viewerWin = window.open(pendingViewerUrl, '_blank');
      if (!viewerWin) {
        hud.setStatus(`popup blocked — open ${pendingViewerUrl} manually (data saved)`);
      }
      recordToggleBtn.disabled = true;
      void wakeLock.release();
      keepTabActive.release();
      // Defence in depth: if recorder.stop / encode / save throws, we
      // *must* still re-enable the button. Otherwise a transient failure
      // (IndexedDB quota, fileSaver cancel, etc.) would strand the user
      // on a permanently disabled Record button.
      try {
        const blob = await recorder.stop();
        // Drain any in-flight inferences whose t0 was <= recordingEndedAt but
        // whose .then() hasn't pushed to the store yet. Wait up to 500ms.
        const drainDeadline = performance.now() + 500;
        while (isInferring && performance.now() < drainDeadline) {
          await new Promise((r) => setTimeout(r, 20));
        }
        if (blob && blob.size > 0) {
          const s = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
          // The recorded WebM's t=0 aligns with the first encoded video frame
          // (muxer uses `firstTimestampBehavior: 'offset'`). Pose records
          // timestamped before that anchor correspond to no video frame, so
          // use the anchor as the pose timeline origin to keep video and
          // skeleton in lockstep on playback.
          const videoT0 = recorder.firstFrameWallclock() ?? recordingStartedAt;
          const poseRecordsAll = poseRecordStore
            .getAll()
            .filter((r) => r.t >= videoT0 && r.t <= recordingEndedAt);
          // Now that we have a stable snapshot, restore the live-analysis
          // cap (whatever the slider is set to). The uncapped state was only
          // needed for the duration of the recording.
          const liveCap = poseRecSlider
            ? Math.max(60, Math.min(1800, Math.floor(Number(poseRecSlider.value))))
            : 600;
          poseRecordStore.setMaxRecords(liveCap);
          profiler.observe('record.pose.frames', poseRecordsAll.length);
          if (poseRecordsAll.length === 0) {
            // Nothing to analyse — the pre-opened viewer tab would show
            // "No recording loaded". Close it to avoid confusion.
            try {
              viewerWin?.close();
            } catch {
              /* ignore — opener may not own the popup in some browsers */
            }
            hud.setStatus('no pose data captured — try recording again');
          }
          if (poseRecordsAll.length > 0) {
            // Auth gate: a recording is about to land in IndexedDB.
            // requireSignIn() resolves true when there's a session
            // cookie on file; otherwise it kicks off the LINE Login
            // redirect (the browser navigates away) and returns false
            // so we bail out of the save chain. The pre-opened viewer
            // tab is closed before the redirect to avoid an orphan.
            const signedIn = await requireSignIn(window.location.pathname);
            if (!signedIn) {
              try {
                viewerWin?.close();
              } catch {
                /* ignore */
              }
              hud.setStatus('LINE ログインが必要です — リダイレクトします');
              return;
            }
            const recId = pendingRecId;
            markRecordingSaving(recId, { expectedFrames: poseRecordsAll.length });
            if (viewerWin) {
              hud.setStatus(`saving recording for analysis (${poseRecordsAll.length} frames)…`);
            }
            try {
              const poseBin = await profiler.measureAsync('record.pose_bin_encode', () =>
                encodePoseBinary(poseRecordsAll, {
                  engineId: engine?.id,
                  engineLabel: engine?.label,
                  source: currentMode,
                  resolution,
                  // Use the video's t=0 anchor so the pose timeline and the
                  // video timeline share the same origin in the viewer.
                  recordingStartedAt: videoT0,
                  angleKeys: JOINT_DEFINITIONS.map((d) => d.key),
                }),
              );
              const canonicalBin = await profiler.measureAsync(
                'record.canonical_build',
                () => tryBuildCanonicalBin(poseBin, composeCanvas),
              );
              await profiler.measureAsync('record.save_indexeddb', () =>
                saveRecording({
                  id: recId,
                  createdAt: Date.now(),
                  videoBlob: blob,
                  videoMime: blob.type,
                  poseBin,
                  canonicalBin,
                  engineId: engine?.id,
                  engineLabel: engine?.label,
                  resolution,
                }),
              );
              markRecordingReady(recId, { frames: poseRecordsAll.length });
              void pruneOldRecordings(3);
              hud.setStatus(`analysis ready · ${poseRecordsAll.length} frames`);
              refreshProfileSummary();
            } catch (err) {
              console.error('[BodyNodeLink] saveRecording', err);
              markRecordingFailed(recId, err instanceof Error ? err.message : String(err));
              hud.setStatus(`save failed: ${err instanceof Error ? err.message : String(err)}`);
            }
          }
          if (hud.getSettings().bundleOnRecord) {
            const zipFiles: Record<string, Uint8Array> = {
              [`recording-${s}.${ext}`]: new Uint8Array(await blob.arrayBuffer()),
            };
            if (poseRecordsAll.length > 0) {
              zipFiles['pose.json'] = strToU8(
                profiler.measure('record.bundle.pose_json', () =>
                  posesToJson(poseRecordsAll, {
                    engineId: engine?.id,
                    engineLabel: engine?.label,
                    source: currentMode,
                    resolution,
                  }),
                ),
              );
              zipFiles['pose-nodes.csv'] = strToU8(
                profiler.measure('record.bundle.pose_nodes_csv', () => nodesToCsv(poseRecordsAll)),
              );
              zipFiles['pose-angles.csv'] = strToU8(
                profiler.measure('record.bundle.pose_angles_csv', () =>
                  anglesToCsv(poseRecordsAll),
                ),
              );
            }
            const trajs = trajectories.getAll();
            if (trajs.length > 0) {
              let trackPoints = 0;
              for (const traj of trajs) trackPoints += traj.points.length;
              profiler.observe('record.bundle.tracking_points', trackPoints);
              zipFiles['tracking.json'] = strToU8(
                profiler.measure('record.bundle.tracking_json', () =>
                  trajectoriesToJson(trajs, { source: currentMode, resolution }),
                ),
              );
              zipFiles['tracking.csv'] = strToU8(
                profiler.measure('record.bundle.tracking_csv', () => trajectoriesToCsv(trajs)),
              );
            }
            const zipped = Uint8Array.from(
              profiler.measure('record.bundle_zip', () => zipSync(zipFiles, { level: 6 })),
            );
            downloadBlob(new Blob([zipped], { type: 'application/zip' }), `bodynodelink-${s}.zip`);
            hud.setStatus(`saved zip bundle (${Object.keys(zipFiles).length} files)`);
            refreshProfileSummary();
          } else {
            const filename = `bodynodelink-${s}.${ext}`;
            const savedToFolder = await fileSaver.save(blob, filename);
            if (!savedToFolder) downloadBlob(blob, filename);
            else hud.setStatus(`saved to chosen folder: ${filename}`);
          }
        }
      } finally {
        // Restore the Record button to its idle state regardless of
        // whether the stop / encode / save chain succeeded. This is the
        // critical guarantee — without it a transient save failure
        // (IndexedDB quota, blob.arrayBuffer rejection, file picker
        // cancel, etc.) would leave Record permanently disabled.
        recordToggleBtn.disabled = false;
        recordToggleBtn.textContent = 'Record';
        recordToggleBtn.classList.remove('recording');
        recordTime.textContent = '0:00';
      }
    } else if (currentMode === 'file') {
      // File mode: Record means "save the original uploaded blob +
      // accumulated pose data straight to IndexedDB". This bypasses
      // composeCanvas / MediaRecorder entirely so the saved video is
      // bit-exact and a stale frame from a previous source can never
      // bleed in.
      recordToggleBtn.disabled = true;
      try {
        await openViewerForCurrentFile();
      } finally {
        recordToggleBtn.disabled = false;
      }
      return;
    } else {
      const w = video.videoWidth || resolution.width;
      const h = video.videoHeight || resolution.height;
      composeCanvas.width = w;
      composeCanvas.height = h;
      // Drop any pose records accumulated before the user pressed Record
      // so pose t=0 lines up with video t=0 in the viewer.
      resetPoseCapture();
      // During an active recording the pose store must keep every record —
      // the live-analysis cap (600 records = ~20s at 30fps) would silently
      // evict the start of long recordings. Restore the cap on stop.
      poseRecordStore.setMaxRecords(Number.POSITIVE_INFINITY);
      recordingStartedAt = performance.now();
      recorder.start();
      void wakeLock.request();
      // Keep this tab exempt from background-tab throttling so rVFC /
      // rAF keep firing at 30–60 Hz even when the user switches to
      // another tab (e.g. to press play on a YouTube video being
      // screen-captured). A silent audio node is the standard trick.
      keepTabActive.acquire();
      recordToggleBtn.textContent = 'Stop';
      recordToggleBtn.classList.add('recording');
    }
  });

  try {
    hud.setStatus('requesting camera…');
    const res = await camera.start({
      width: 1280,
      height: 720,
      facingMode: 'user',
    });
    resolution = res;
    renderer.resize(res.width, res.height);

    // Start video→composeCanvas transfer before awaiting the model so
    // recording works even while the (potentially 30MB) heavy model loads.
    const rvfcSupported =
      typeof (
        video as HTMLVideoElement & {
          requestVideoFrameCallback?: (
            cb: (now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void,
          ) => number;
        }
      ).requestVideoFrameCallback === 'function';
    // rVFC frame-skip monitor (matches the mobile import diagnostic).
    // Tracks the previous fire's mediaTime; when a gap exceeds 1.75x
    // the rolling-median delta we count it as a decoder-skipped frame
    // and surface via a throttled console.warn so users observing
    // "tick" in the saved skeleton can confirm whether the cause is
    // decoder loss vs adapter latency vs source fps.
    let prevComposeMediaT: number | null = null;
    const composeMediaDeltas: number[] = [];
    let composeDroppedCount = 0;
    let composeDropWarnedAt = 0;
    const composeOnVideoFrame = (
      _now?: DOMHighResTimeStamp,
      metadata?: VideoFrameCallbackMetadata,
    ): void => {
      if (metadata && typeof metadata.mediaTime === 'number') {
        const t = metadata.mediaTime;
        if (prevComposeMediaT !== null) {
          const dt = t - prevComposeMediaT;
          if (dt > 0 && dt < 1) {
            composeMediaDeltas.push(dt);
            // Bound the rolling window; the median is what we care about.
            if (composeMediaDeltas.length > 30) composeMediaDeltas.shift();
            if (composeMediaDeltas.length >= 5) {
              const sorted = [...composeMediaDeltas].sort((a, b) => a - b);
              const median = sorted[Math.floor(sorted.length / 2)];
              if (median > 0 && dt > median * 1.75) {
                const skipped = Math.max(1, Math.round(dt / median) - 1);
                composeDroppedCount += skipped;
                const nowMs = performance.now();
                if (nowMs - composeDropWarnedAt > 5000) {
                  composeDropWarnedAt = nowMs;
                  console.warn(
                    '[main] decoder skipped ~',
                    composeDroppedCount,
                    'video frames (cumulative). Pose density may dip on this stretch.',
                  );
                }
              }
            }
          }
        }
        prevComposeMediaT = t;
      }
      if (recorder.isRecording() && video.readyState >= 2 && video.videoWidth > 0) {
        const settings = hud.getSettings();
        // IMPORTANT: once recording has started, the encoder is locked to
        // a fixed input resolution. If the source video's dimensions change
        // mid-recording (e.g. the user switches Source from File→Camera or
        // the file finishes loading after Record began), resizing
        // composeCanvas would make every subsequent captureFrame() bail out
        // with "canvas dimensions changed; frame dropped". That produced
        // ~1-frame videos. Only resize the canvas when NOT recording.
        if (!recorder.isRecording() || composeCanvas.width === 0 || composeCanvas.height === 0) {
          if (
            composeCanvas.width !== video.videoWidth ||
            composeCanvas.height !== video.videoHeight
          ) {
            composeCanvas.width = video.videoWidth;
            composeCanvas.height = video.videoHeight;
          }
        }
        composeCtx.save();
        if (settings.mirror) {
          composeCtx.translate(composeCanvas.width, 0);
          composeCtx.scale(-1, 1);
        }
        // Draw the (possibly differently-sized) source video stretched
        // into the locked composeCanvas. Object-fit is cover-ish; it's
        // less distorted than dropping every frame.
        composeCtx.drawImage(video, 0, 0, composeCanvas.width, composeCanvas.height);
        composeCtx.restore();
        composeCtx.drawImage(canvas, 0, 0, composeCanvas.width, composeCanvas.height);
        // WebCodecs backend requires an explicit per-frame capture call.
        // MediaRecorder backend treats this as a no-op.
        recorder.captureFrame();
      }
      if (rvfcSupported) {
        (
          video as unknown as {
            requestVideoFrameCallback: (cb: () => void) => number;
          }
        ).requestVideoFrameCallback(composeOnVideoFrame);
      } else {
        requestAnimationFrame(composeOnVideoFrame);
      }
    };
    if (rvfcSupported) {
      (
        video as unknown as {
          requestVideoFrameCallback: (cb: () => void) => number;
        }
      ).requestVideoFrameCallback(composeOnVideoFrame);
    } else {
      requestAnimationFrame(composeOnVideoFrame);
    }

    const quality = hud.getSettings().poseModelQuality;
    hud.setStatus(
      quality === 'heavy'
        ? 'loading heavy model (~30MB, up to 45s on first load)…'
        : 'loading model…',
    );
    await engine.initialize();
    refreshCropOrchestrator();
    refreshOnnxEpBadge();

    hud.setStatus('running');
    applyMirror(video, hud.getSettings().mirror);
    recordToggleBtn.disabled = false;
    recordToggleBtn.title = '';

    const loop = (): void => {
      const settings = hud.getSettings();

      const now = performance.now();
      frameTimestamps.push(now);
      while (frameTimestamps.length && now - frameTimestamps[0] > 1000) {
        frameTimestamps.shift();
      }
      const fps = frameTimestamps.length;

      const hasFrame = video.readyState >= 2 && video.videoWidth > 0;

      const canInfer = !isInferring && !isSwitching && hasFrame && perf.shouldInfer();

      if (canInfer && settings.appMode === 'pose' && engine) {
        isInferring = true;
        const activeEngine = engine;
        const t0 = performance.now();
        const sourceModeAtCapture = currentMode;
        const fileFrameTimeMs =
          sourceModeAtCapture === 'file' && Number.isFinite(video.currentTime)
            ? Math.max(0, video.currentTime * 1000)
            : null;
        if (
          sourceModeAtCapture === 'file' &&
          fileFrameTimeMs !== null &&
          fileFrameTimeMs + 50 < lastFilePoseTimeMs
        ) {
          resetPoseAnalysisState();
          hud.setStatus('file playback restarted — restarted pose capture for viewer');
        }
        // Watchdog: if the inference engine (especially a worker) hangs
        // mid-recording, `isInferring` would stick at true and no further
        // poses would ever be recorded — while the video keeps being
        // captured. Force-release after 3s so recording recovers.
        let watchdogFired = false;
        const watchdog = window.setTimeout(() => {
          watchdogFired = true;
          isInferring = false;
          console.warn('[BodyNodeLink] inference watchdog timed out after 3s; resetting');
          profiler.observe('pose.watchdog_timeouts', 1);
        }, 3000);
        const detectPromise =
          settings.useCropPipeline && cropOrchestrator
            ? cropOrchestrator.run(video, t0)
            : Promise.resolve(activeEngine.detectForVideo(video, t0));
        detectPromise
          .then((raw) => {
            if (watchdogFired) return; // dropped result from a stuck engine
            lastInferenceMs = performance.now() - t0;
            profiler.recordTiming('pose.detect', lastInferenceMs);
            profiler.observe('pose.people', raw.poses.length);
            const mapped = profiler.measure('pose.map_raw', () =>
              mapRawToBodies(raw, activeEngine.skeleton.nodeNames, activeEngine.skeleton.links, {
                fps,
                inferenceTimeMs: lastInferenceMs,
              }),
            );
            const smoothed = settings.smoothPose
              ? profiler.measure('pose.smooth', () => poseSmoother.apply(mapped, performance.now()))
              : mapped;
            latest = smoothed.map((body) => filterByScore(body, settings.minScore));
            hud.pushInferSample(lastInferenceMs);
            const primaryRaw = smoothed[settings.primaryIndex] ?? smoothed[0];
            const anglesPerPerson = profiler.measure('pose.compute_angles', () =>
              smoothed.map((p) =>
                computeJointAngles(p, settings.minScore, activeEngine.skeleton.jointDefs),
              ),
            );
            const primaryAngles =
              smoothed.length > 0
                ? anglesPerPerson[Math.min(settings.primaryIndex, smoothed.length - 1)]
                : undefined;
            if (primaryRaw) {
              hud.updateNodeScores(primaryRaw);
              hud.updateAngles(primaryAngles ?? {});
            } else {
              hud.updateAngles({});
            }
            if (smoothed.length > 0) {
              // Use the inference *capture* time (t0) rather than the completion
              // time so each pose record lines up with the video frame it was
              // computed from. Using completion time drifts the skeleton
              // ~inference-latency ms behind the video and also causes the
              // final frames of the video to have no skeleton because their
              // inferences complete after `recordingEndedAt`.
              const poseTimestampMs =
                sourceModeAtCapture === 'file' && fileFrameTimeMs !== null ? fileFrameTimeMs : t0;
              profiler.measure('pose.record_store_push', () =>
                poseRecordStore.push(smoothed, primaryAngles, poseTimestampMs, anglesPerPerson),
              );
              if (sourceModeAtCapture === 'file' && fileFrameTimeMs !== null) {
                lastFilePoseTimeMs = fileFrameTimeMs;
                refreshFileViewerButton();
              }
            }
          })
          .catch((err) => {
            console.error('[BodyNodeLink] inference error', err);
          })
          .finally(() => {
            window.clearTimeout(watchdog);
            if (!watchdogFired) isInferring = false;
          });
      } else if (canInfer && settings.appMode === 'tracking' && detector) {
        isInferring = true;
        const activeDetector = detector;
        activeDetector
          .detect(video)
          .then(async (result) => {
            lastInferenceMs = result.inferenceTimeMs;
            profiler.recordTiming('track.detect', result.inferenceTimeMs);
            const nowTs = performance.now();
            const detections = settings.personsOnly
              ? result.detections.filter((d) => d.class === 'person')
              : result.detections;
            profiler.observe('tracking.detections', detections.length);
            const features = new Map<number, ReIdFeature>();
            const useDeep = settings.useDeepReid && deepReidExtractor.isReady();
            const personEntries: { i: number; det: (typeof detections)[number] }[] = [];
            for (let i = 0; i < detections.length; i += 1) {
              const det = detections[i];
              if (det.class === 'person') personEntries.push({ i, det });
            }
            profiler.observe('tracking.person_detections', personEntries.length);
            const extracted = await profiler.measureAsync('track.feature_extract', () =>
              Promise.all(
                personEntries.map(async ({ i, det }) => ({
                  i,
                  f: useDeep
                    ? await deepReidExtractor.extract(video, det)
                    : sampleFeature(video, det, reidSampleCanvas),
                })),
              ),
            );
            for (const { i, f } of extracted) if (f) features.set(i, f);
            profiler.observe('tracking.features', features.size);
            const { tracks, removed } = profiler.measure('track.update', () =>
              tracker.update(detections, nowTs, features),
            );
            profiler.observe('tracking.tracks', tracks.length);
            profiler.measure('track.trajectories', () => {
              trajectories.drop(removed);
              trajectories.append(tracks, nowTs);
            });
            teamClassifier.drop(removed);
            trackTags.drop(removed);
            if (settings.showTeams) {
              profiler.measure('track.team_sampling', () => {
                for (const t of tracks) {
                  if (t.class !== 'person' || t.isGhost) continue;
                  const sample = sampleUpperBodyColor(video, t.box, {
                    offscreen: colorSampleCanvas,
                  });
                  if (sample) teamClassifier.observe(t.id, sample);
                }
              });
              framesSinceClassify += 1;
              if (framesSinceClassify >= 15) {
                profiler.measure('track.team_classify', () => teamClassifier.classify());
                framesSinceClassify = 0;
              }
            }
            hud.pushInferSample(lastInferenceMs);
            hud.setTrackingStats(detections.length, tracks.length);
            hud.setGoneCount(tracker.getGoneCount());
          })
          .catch((err) => {
            console.error('[BodyNodeLink] tracking error', err);
          })
          .finally(() => {
            isInferring = false;
          });
      }

      if (settings.appMode === 'pose' && latest.length > 0 && engine) {
        const activeEngine = engine;
        const primary = latest[settings.primaryIndex] ?? latest[0];
        profiler.measure('pose.render', () =>
          renderer.draw(latest, {
            mirror: settings.mirror,
            minScore: settings.minScore,
            showLabels: settings.showLabels,
            showAngles: settings.showAngles,
            colorizeByScore: settings.colorizeByScore,
            primaryIndex: Math.min(settings.primaryIndex, latest.length - 1),
            angles: primary
              ? computeJointAngles(primary, settings.minScore, activeEngine.skeleton.jointDefs)
              : undefined,
            jointDefs: activeEngine.skeleton.jointDefs,
          }),
        );
        hud.updateMetrics({
          fps,
          inferenceTimeMs: lastInferenceMs,
          activeNodes: primary.nodes.filter((n) => n.score >= settings.minScore).length,
          totalNodes: primary.nodes.length,
          resolution,
        });
      } else if (settings.appMode === 'tracking') {
        let teamColors: Map<number, string> | undefined;
        if (settings.showTeams) {
          teamColors = new Map();
          for (const t of tracker.getTracks()) {
            const a = teamClassifier.assignmentFor(t.id);
            if (a) teamColors.set(t.id, a.color);
          }
        }
        const courtHomography = courtCalibrator.getHomography();
        const useCourtHeatmap = settings.heatmapOnCourt && !!courtHomography;
        if (settings.showHeatmap) {
          framesSinceHeatmap += 1;
          if (framesSinceHeatmap >= 10) {
            if (useCourtHeatmap && courtHomography) {
              let totalPoints = 0;
              for (const traj of trajectories.getAll()) totalPoints += traj.points.length;
              profiler.observe('heatmap.points', totalPoints);
              profiler.measure('heatmap.accumulate_on_court', () =>
                heatmapRenderer.accumulateOnCourt(
                  trajectories.getAll(),
                  courtHomography,
                  BASKETBALL_COURT.widthMeters,
                  BASKETBALL_COURT.heightMeters,
                ),
              );
            } else {
              let totalPoints = 0;
              for (const traj of trajectories.getAll()) totalPoints += traj.points.length;
              profiler.observe('heatmap.points', totalPoints);
              profiler.measure('heatmap.accumulate', () =>
                heatmapRenderer.accumulate(trajectories.getAll()),
              );
            }
            framesSinceHeatmap = 0;
          }
        }
        profiler.measure('tracking.render', () =>
          trackingRenderer.draw(tracker.getTracks(), trajectories.getAll(), {
            mirror: settings.mirror,
            showBoxes: settings.showBoxes,
            showLabels: settings.showLabels,
            showTrajectories: settings.showTrails,
            showGhosts: settings.showGhosts,
            showHeatmap: settings.showHeatmap && !useCourtHeatmap,
            minScore: settings.minScore,
            teamColors,
            heatmap: heatmapRenderer,
            tags: trackTags.entries(),
          }),
        );
        if (settings.showEdges && edgeScratchCtx) {
          const edges = profiler.measure('edge.detect', () => edgeDetector.detect(video));
          if (edges) {
            edgeScratchCtx.putImageData(edges, 0, 0);
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.save();
              if (settings.mirror) {
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
              }
              ctx.globalAlpha = 0.7;
              ctx.drawImage(edgeScratch, 0, 0, canvas.width, canvas.height);
              ctx.restore();
            }
          }
        }
        if (courtHomography) {
          profiler.measure('court.draw', () =>
            courtView.draw(
              tracker.getTracks(),
              trajectories.getAll(),
              courtHomography,
              teamColors,
              useCourtHeatmap && settings.showHeatmap ? heatmapRenderer : undefined,
            ),
          );
        } else {
          courtView.clear();
        }
        hud.updateMetrics({
          fps,
          inferenceTimeMs: lastInferenceMs,
          activeNodes: 0,
          totalNodes: 0,
          resolution,
        });
      }

      // Recording time display only — actual composition is driven by rVFC
      // in composeOnVideoFrame() so heavy inference can't choke video fps.
      if (recorder.isRecording()) {
        recordTime.textContent = formatTime(recorder.elapsedMs() / 1000);
      }

      if (now - lastProfileSummaryAt >= 1000) {
        profiler.observe('runtime.fps', fps);
        refreshProfileSummary();
        lastProfileSummaryAt = now;
      }

      requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    hud.setStatus(`error: ${message}`);
    console.error('[BodyNodeLink]', err);
  }
}

function applyMirror(video: HTMLVideoElement, mirror: boolean): void {
  video.style.transform = mirror ? 'scaleX(-1)' : 'none';
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

void main();
